import { useState, useMemo } from 'react';
import Select from 'components/ui/Select';
import Card from 'components/ui/Card';
import BaseButton from 'components/ui/BaseButton';
import { PaperPlane, ArrowRight } from 'assets/icons/revamp';
import { t } from 'services/i18n.service';
import { toast } from 'react-toastify';
import {
  ACTIVITY_TYPES,
  ROUTINE_DESTINATION_POSITION
} from 'constants/general';
import { DEFAULT_ROUTINE_DESTINATION_OPTIONS } from 'assets/data';
import { getRoutineActivities } from 'utils/helpers';
import {
  getCustomRoutineIdFromOptionValue,
  isCustomRoutineOptionValue,
  resolveRoutineDestination,
  toCustomRoutineOptionValue
} from 'utils/routine-destination.util';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { moreOptionModalActionsSelector } from 'store/reducer/modal/more-options.modal.selectors';
import { updateRoutineCategory } from 'store/reducer/setting/slice';

const MoveActivity: React.FC = () => {
  const dispatch = useAppDispatch();
  const { moreOptions, moreOption, currentSettings, customRoutines } =
    useAppSelectorShallow(moreOptionModalActionsSelector);
  const activity = moreOption.activity;
  const type = moreOptions.type;
  const position = moreOptions.position;
  const activitySequenceId = activity?.activity_sequence_id ?? '';

  const [selectedRoutine, setSelectedRoutine] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(
    ROUTINE_DESTINATION_POSITION.START
  );

  const currentRoutine = useMemo(() => {
    const { custom_routines: routines } = currentSettings;
    if (!Array.isArray(routines) || !activitySequenceId) {
      return undefined;
    }
    return routines.find(
      (routine) => routine.activity_sequence_id === activitySequenceId
    );
  }, [activitySequenceId, currentSettings]);

  const customRoutineIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    customRoutines?.forEach((routine, idx) => {
      if (routine.activity_sequence_id) {
        map.set(routine.activity_sequence_id, idx);
      }
    });
    return map;
  }, [customRoutines]);

  const destinationOptions = useMemo(() => {
    const builtIns = getRoutineActivities(type).map((src) => ({
      label: t(src.label),
      value: src.value
    }));

    const dropDownOptionsForCustomRoutines = (customRoutines ?? [])
      .filter((routine) => routine.id !== currentRoutine?.id)
      .map((routine) => ({
        label: routine.name || t('custom_routine'),
        value: toCustomRoutineOptionValue(routine.id)
      }));

    return [...builtIns, ...dropDownOptionsForCustomRoutines];
  }, [type, customRoutines, currentRoutine?.id]);

  const positionOptions = DEFAULT_ROUTINE_DESTINATION_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.label)
  }));

  const isDestinationCustom = isCustomRoutineOptionValue(selectedRoutine);
  const destinationCustomId = isDestinationCustom
    ? getCustomRoutineIdFromOptionValue(selectedRoutine)
    : '';
  const destinationRoutine = useMemo(
    () => customRoutines?.find((routine) => routine.id === destinationCustomId),
    [destinationCustomId, customRoutines]
  );
  const sourceCustomIndex = customRoutineIndexMap.get(activitySequenceId) ?? -1;

  const getDestinationCategoryName = () =>
    isDestinationCustom
      ? destinationRoutine?.name || t('custom_routine')
      : selectedRoutine.replace('_', ' ');

  const handleMoveHabit = () => {
    if (!activity?.name || !selectedRoutine) {
      return;
    }
    const destination = resolveRoutineDestination(
      selectedRoutine,
      currentSettings
    );
    if (!destination) {
      return;
    }

    const isSourceCustom = Boolean(currentRoutine);
    dispatch(
      updateRoutineCategory({
        type: isSourceCustom ? ACTIVITY_TYPES.CUSTOM_ROUTINES : type,
        position,
        ...destination,
        destination_position: selectedPosition,
        ...(isSourceCustom && { customRoutinePosition: sourceCustomIndex })
      })
    );

    toast.success(
      t('move_habit.completed', {
        name: activity.name,
        category: getDestinationCategoryName(),
        position: selectedPosition
      })
    );
  };

  return (
    <Card className='flex flex-col ~gap-3/4'>
      <div className='flex items-start ~gap-2/3'>
        <PaperPlane
          styles='~w-3/4 ~h-3/4 flex-shrink-0 ~mt-0.5/1 text-primary-500 dark:text-primary-dark-500'
          fill='currentColor'
          fillDarkMode='currentColor'
        />
        <h3 className='~text-xs/sm font-semibold text-black dark:text-white flex flex-col'>
          {t('tabs_revamp.advanced_tab.move_to_different_routine')}
          <span className='~text-[0.625rem]/xs text-gray-600 dark:text-gray-400 font-normal'>
            {t(
              'tabs_revamp.advanced_tab.move_to_different_routine_description'
            )}
          </span>
        </h3>
      </div>
      <div className='flex items-center ~gap-3/6 flex-wrap'>
        <Select
          value={selectedRoutine}
          options={destinationOptions}
          onChange={(value) => setSelectedRoutine(value as string)}
          placeholder={t('tabs_revamp.advanced_tab.select_a_routine')}
          className='w-full ~min-h-4/8'
          isFullWidth={true}
          size='sm'
        />

        <Select
          value={selectedPosition}
          options={positionOptions}
          onChange={(value) => setSelectedPosition(value as string)}
          placeholder={t('move_habit.where_should_it_be_placed')}
          className='w-full ~min-h-4/8'
          isFullWidth={true}
          size='sm'
        />
        <BaseButton
          title={t('tabs_revamp.advanced_tab.move_habit')}
          onClick={handleMoveHabit}
          size='xs'
          icon={
            <ArrowRight
              styles={`~w-3/4 ~h-3/4 text-primary-light-50 dark:text-primary-dark-900 ${selectedRoutine ? 'text-primary-light-50 dark:text-primary-dark-900' : 'text-secondary-dark-900 dark:text-secondary-dark-500'}`}
              fill='currentColor'
              fillDarkMode='currentColor'
            />
          }
          disabled={!selectedRoutine}
          additionalStyles='ml-auto'
        />
      </div>
    </Card>
  );
};

export default MoveActivity;
